/**
 * Market Data Redux Slice
 * Manages market data state (instruments, live quotes, watchlist)
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this slice only stores market data from backend.
 * All market data is provided by backend Instrument Service.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import { Instrument } from '../../types/instrument';

export interface Quote {
  instrument_token: string;
  symbol: string;
  last_price: number;
  change: number;
  change_percentage: number;
  volume: number;
  timestamp: string;
}

export interface WatchlistGroup {
  id: string;
  name: string;
  instruments: string[]; // Array of instrument_ids for this group
  item_count: number;    // Total items from backend (authoritative after groups load)
  is_default?: boolean;  // Whether this is the default group
  itemsLoaded: boolean;  // True once items have been fetched from the backend for this group
}

export interface MarketDataState {
  instruments: Record<string, Instrument>; // Normalized by instrument_id
  searchResults: Instrument[]; // Current search results
  searchQuery: string;
  liveQuotes: Record<string, Quote>;
  watchlistGroups: Record<string, WatchlistGroup>; // Groups keyed by id
  currentGroup: string | null;
  subscriptions: string[];
  loading: boolean;
  searchLoading: boolean;
  watchlistLoading: boolean; // Loading state for watchlist operations
  watchlistError: string | null; // Error state for watchlist operations
  error: string | null;
}

const initialState: MarketDataState = {
  instruments: {},
  searchResults: [],
  searchQuery: '',
  liveQuotes: {},
  watchlistGroups: {},
  currentGroup: null,
  subscriptions: [],
  loading: false,
  searchLoading: false,
  watchlistLoading: false,
  watchlistError: null,
  error: null,
};

/**
 * Load all watchlist groups for the authenticated user
 */
export const loadWatchlistGroups = createAsyncThunk(
  'marketData/loadWatchlistGroups',
  async (_, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.getAllWatchlistGroups();

      if (response.data?.success && response.data?.data?.groups) {
        return response.data.data.groups;
      }
      return [];
    } catch (error: unknown) {
      let errorMessage = 'Failed to load watchlist groups';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        const axiosError = error as any;
        if (axiosError.response) {
          errorMessage = `API Error: ${axiosError.response.status} - ${axiosError.response.data?.message || axiosError.response.statusText}`;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Load watchlist items for a specific group
 */
export const loadWatchlistItems = createAsyncThunk(
  'marketData/loadWatchlistItems',
  async (groupId: string, { rejectWithValue }) => {
    try {
      // eslint-disable-next-line no-console
      console.log('loadWatchlistItems: Starting to load items for group:', groupId);
      const { api } = await import('../../api/client');
      const response = await api.getAllWatchlistItems(groupId);

      // eslint-disable-next-line no-console
      console.log('loadWatchlistItems: API response:', response.data);

      if (response.data?.success && response.data?.data?.items) {
        const items = response.data.data.items;
        // eslint-disable-next-line no-console
        console.log('loadWatchlistItems: Items received:', items);
        const instrumentIds = items.map((item: any) => item.instrument_id).filter(Boolean);
        // eslint-disable-next-line no-console
        console.log('loadWatchlistItems: Extracted instrument IDs:', instrumentIds);

        // Fetch instrument details for all instrument IDs
        let instruments: Instrument[] = [];
        if (instrumentIds.length > 0) {
          try {
            // eslint-disable-next-line no-console
            console.log('Fetching instrument details for IDs:', instrumentIds);
            const instrumentResponse = await api.instrumentBatch({
              instrument_ids: instrumentIds,
              include_mappings: true,
              broker_type: 'zerodha',
            });

            // eslint-disable-next-line no-console
            console.log('Instrument batch response:', instrumentResponse.data);

            const responseData = instrumentResponse.data;
            const instrumentsArray = responseData?.instruments || responseData?.data?.instruments || [];

            if (instrumentsArray.length > 0) {
              instruments = instrumentsArray.map((inst: any) => {
                // Resolve nested broker_instrument_token from broker_mapping(s)
                const brokerMapping = inst.broker_mapping || (inst.broker_mappings && inst.broker_mappings[0]);
                const brokerInstrumentToken = brokerMapping?.broker_instrument_token || inst.broker_instrument_token;

                return {
                  instrument_id: inst.instrument_id || inst.id || '',
                  symbol: inst.symbol || '',
                  exchange: inst.exchange || '',
                  instrument_type: inst.instrument_type || '',
                  broker_instrument_token: brokerInstrumentToken,
                  name: inst.name || '',
                  lot_size: inst.lot_size ? parseFloat(String(inst.lot_size)) : undefined,
                  tick_size: inst.tick_size ? parseFloat(String(inst.tick_size)) : undefined,
                  expiry_date: inst.expiry_date,
                  strike_price: inst.strike_price ? parseFloat(String(inst.strike_price)) : undefined,
                  option_type: inst.option_type,
                };
              });
              // eslint-disable-next-line no-console
              console.log('Mapped instruments:', instruments);
            } else {
              // eslint-disable-next-line no-console
              console.warn('No instruments in response:', instrumentResponse.data);
            }
          } catch (instrumentError) {
            // Log error but don't fail the entire operation
            // eslint-disable-next-line no-console
            console.error('Failed to fetch instrument details:', instrumentError);
            if (instrumentError instanceof Error) {
              // eslint-disable-next-line no-console
              console.error('Error message:', instrumentError.message);
              // eslint-disable-next-line no-console
              console.error('Error stack:', instrumentError.stack);
            }
            if (typeof instrumentError === 'object' && instrumentError !== null) {
              const axiosError = instrumentError as any;
              if (axiosError.response) {
                // eslint-disable-next-line no-console
                console.error('API Error Response:', axiosError.response.data);
                // eslint-disable-next-line no-console
                console.error('API Error Status:', axiosError.response.status);
              }
            }
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn('No instrument IDs to fetch');
        }

        // eslint-disable-next-line no-console
        console.log('loadWatchlistItems: Returning payload with', instruments.length, 'instruments');
        return {
          groupId,
          items,
          instruments,
        };
      }
      // eslint-disable-next-line no-console
      console.warn('loadWatchlistItems: No items in response or response structure unexpected');
      return { groupId, items: [], instruments: [] };
    } catch (error: unknown) {
      let errorMessage = 'Failed to load watchlist items';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        const axiosError = error as any;
        if (axiosError.response) {
          errorMessage = `API Error: ${axiosError.response.status} - ${axiosError.response.data?.message || axiosError.response.statusText}`;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Create a new watchlist group
 */
export const createWatchlistGroup = createAsyncThunk(
  'marketData/createWatchlistGroup',
  async (params: { name: string; displayOrder?: number; isDefault?: boolean }, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.createWatchlistGroup({
        name: params.name,
        display_order: params.displayOrder,
        is_default: params.isDefault,
      });

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }
      throw new Error('Invalid response from server');
    } catch (error: unknown) {
      let errorMessage = 'Failed to create watchlist group';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        const axiosError = error as any;
        if (axiosError.response) {
          errorMessage = `API Error: ${axiosError.response.status} - ${axiosError.response.data?.message || axiosError.response.statusText}`;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update a watchlist group
 */
export const updateWatchlistGroup = createAsyncThunk(
  'marketData/updateWatchlistGroup',
  async (params: { groupId: string; name?: string; displayOrder?: number; isDefault?: boolean }, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.updateWatchlistGroup(params.groupId, {
        name: params.name,
        display_order: params.displayOrder,
        is_default: params.isDefault,
      });

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }
      throw new Error('Invalid response from server');
    } catch (error: unknown) {
      let errorMessage = 'Failed to update watchlist group';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        const axiosError = error as any;
        if (axiosError.response) {
          errorMessage = `API Error: ${axiosError.response.status} - ${axiosError.response.data?.message || axiosError.response.statusText}`;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Delete a watchlist group
 */
export const deleteWatchlistGroupAsync = createAsyncThunk(
  'marketData/deleteWatchlistGroup',
  async (groupId: string, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      await api.deleteWatchlistGroup(groupId);
      return groupId;
    } catch (error: unknown) {
      let errorMessage = 'Failed to delete watchlist group';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        const axiosError = error as any;
        if (axiosError.response) {
          errorMessage = `API Error: ${axiosError.response.status} - ${axiosError.response.data?.message || axiosError.response.statusText}`;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Add an instrument to a watchlist group
 */
export const addWatchlistItem = createAsyncThunk(
  'marketData/addWatchlistItem',
  async (params: { groupId: string; instrumentId: string; displayOrder?: number }, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.addWatchlistItem(params.groupId, {
        instrument_id: params.instrumentId,
        display_order: params.displayOrder,
      });

      if (response.data?.success && response.data?.data) {
        return {
          groupId: params.groupId,
          item: response.data.data,
        };
      }
      throw new Error('Invalid response from server');
    } catch (error: unknown) {
      let errorMessage = 'Failed to add instrument to watchlist';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        const axiosError = error as any;
        if (axiosError.response) {
          errorMessage = `API Error: ${axiosError.response.status} - ${axiosError.response.data?.message || axiosError.response.statusText}`;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Remove an instrument from a watchlist group
 */
export const removeWatchlistItem = createAsyncThunk(
  'marketData/removeWatchlistItem',
  async (params: { groupId: string; instrumentId: string }, { rejectWithValue, getState }) => {
    try {
      const { api } = await import('../../api/client');
      const { selectFirebaseUid } = await import('../selectors/authSelectors');
      const state = getState() as RootState;
      const userId = selectFirebaseUid(state);

      if (!userId) {
        throw new Error('User not authenticated');
      }

      await api.removeWatchlistItemByInstrument(userId, params.groupId, params.instrumentId);
      return {
        groupId: params.groupId,
        instrumentId: params.instrumentId,
      };
    } catch (error: unknown) {
      let errorMessage = 'Failed to remove instrument from watchlist';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        const axiosError = error as any;
        if (axiosError.response) {
          errorMessage = `API Error: ${axiosError.response.status} - ${axiosError.response.data?.message || axiosError.response.statusText}`;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Search instruments (used by instrument sidebar and other search UIs).
 * Do not pass include_mappings: true here — backend defaults to false for performance;
 * use include_mappings only in admin/reference instrument list.
 */
export const searchInstruments = createAsyncThunk(
  'marketData/searchInstruments',
  async (params: { query: string; exchange?: string; segment?: string; limit?: number }, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.instrumentSearch(
        params.query,
        params.exchange,
        params.segment,
        undefined,        // sector
        undefined,        // instrument_type
        undefined,        // is_active (backend defaults to true)
        params.limit || 20
      );

      // Log response for debugging
      // eslint-disable-next-line no-console
      console.log('Instrument search response:', response.data);

      // Check if response has the expected structure
      // The response might be response.data or response.data.data depending on backend
      const responseData = response.data;
      const instrumentsArray = responseData?.instruments || responseData?.data?.instruments || [];

      // Extract IDs to fetch full details including broker_mappings
      const instrumentIds = instrumentsArray.map((inst: any) => inst.instrument_id || inst.id).filter(Boolean);

      let fullInstruments: any[] = [];
      if (instrumentIds.length > 0) {
        try {
          const batchResponse = await api.instrumentBatch({
            instrument_ids: instrumentIds,
            include_mappings: true,
            broker_type: 'zerodha',
          });
          const responseData = batchResponse.data;
          fullInstruments = responseData?.instruments || responseData?.data?.instruments || [];
        } catch (err) {
          console.warn('Failed to fetch full instrument details for search results:', err);
          // Fallback to original search results
          fullInstruments = instrumentsArray;
        }
      }

      // Map backend response to frontend types
      const instruments: Instrument[] = fullInstruments.map((inst: any) => {
        // Resolve nested broker_instrument_token from broker_mapping(s)
        // Backend now explicitly filters and guarantees only the 'zerodha' mapping is returned in the array
        const brokerMapping = inst.broker_mapping || (inst.broker_mappings && inst.broker_mappings[0]);
        const brokerInstrumentToken = brokerMapping?.broker_instrument_token || inst.broker_instrument_token;

        return {
          instrument_id: inst.instrument_id || '',
          symbol: inst.symbol || '',
          exchange: inst.exchange,
          instrument_type: inst.instrument_type,
          broker_instrument_token: brokerInstrumentToken,
          name: inst.name,
          lot_size: inst.lot_size ? parseFloat(String(inst.lot_size)) : undefined,
          tick_size: inst.tick_size ? parseFloat(String(inst.tick_size)) : undefined,
          expiry_date: inst.expiry_date,
          strike_price: inst.strike_price ? parseFloat(String(inst.strike_price)) : undefined,
          option_type: inst.option_type,
        };
      });

      // eslint-disable-next-line no-console
      console.log('Mapped instruments:', instruments);

      return { instruments, query: params.query };
    } catch (error: unknown) {
      // Enhanced error logging
      let errorMessage = 'Failed to search instruments';
      if (error instanceof Error) {
        errorMessage = error.message;
        // eslint-disable-next-line no-console
        console.error('Instrument search error:', error);
      } else if (typeof error === 'object' && error !== null) {
        const axiosError = error as any;
        if (axiosError.response) {
          errorMessage = `API Error: ${axiosError.response.status} - ${axiosError.response.data?.message || axiosError.response.statusText}`;
          // eslint-disable-next-line no-console
          console.error('API Error Response:', axiosError.response.data);
        } else if (axiosError.request) {
          errorMessage = 'Network Error: No response from server';
          // eslint-disable-next-line no-console
          console.error('Network Error:', axiosError.request);
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const marketDataSlice = createSlice({
  name: 'marketData',
  initialState,
  reducers: {
    updateQuote: (state, action: { payload: Quote }) => {
      state.liveQuotes[action.payload.instrument_token] = action.payload;
    },
    updateQuotes: (state, action: { payload: Quote[] }) => {
      action.payload.forEach((quote) => {
        state.liveQuotes[quote.instrument_token] = quote;
      });
    },
    addToWatchlist: (state, action: { payload: string }) => {
      if (state.currentGroup && state.watchlistGroups[state.currentGroup]) {
        const group = state.watchlistGroups[state.currentGroup];
        if (!group.instruments.includes(action.payload)) {
          group.instruments.push(action.payload);
        }
      }
    },
    removeFromWatchlist: (state, action: { payload: string }) => {
      if (state.currentGroup && state.watchlistGroups[state.currentGroup]) {
        const group = state.watchlistGroups[state.currentGroup];
        group.instruments = group.instruments.filter((id) => id !== action.payload);
      }
    },
    setWatchlistGroup: (state, action: { payload: string }) => {
      if (state.watchlistGroups[action.payload]) {
        state.currentGroup = action.payload;
      }
    },
    addWatchlistGroup: (state, action: { payload: { id: string; name: string } }) => {
      if (!state.watchlistGroups[action.payload.id]) {
        state.watchlistGroups[action.payload.id] = {
          id: action.payload.id,
          name: action.payload.name,
          instruments: [],
          item_count: 0,
          itemsLoaded: false,
        };
      }
    },
    updateWatchlistGroupName: (state, action: { payload: { id: string; name: string } }) => {
      if (state.watchlistGroups[action.payload.id]) {
        state.watchlistGroups[action.payload.id].name = action.payload.name;
      }
    },
    deleteWatchlistGroup: (state, action: { payload: string }) => {
      const groupId = action.payload;
      const groupCount = Object.keys(state.watchlistGroups).length;

      // Don't allow deleting if it's the last group
      if (groupCount <= 1) {
        return;
      }

      // If deleting the current group, switch to another group
      if (state.currentGroup === groupId) {
        const remainingGroups = Object.keys(state.watchlistGroups).filter(id => id !== groupId);
        state.currentGroup = remainingGroups[0] || null;
      }

      // Delete the group
      delete state.watchlistGroups[groupId];
    },
    registerInstruments: (state, action: { payload: Instrument[] }) => {
      action.payload.forEach((inst) => {
        if (inst.instrument_id) {
          state.instruments[inst.instrument_id] = inst;
        }
      });
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = '';
    },
    addSubscription: (state, action: { payload: string }) => {
      if (!state.subscriptions.includes(action.payload)) {
        state.subscriptions.push(action.payload);
      }
    },
    removeSubscription: (state, action: { payload: string }) => {
      state.subscriptions = state.subscriptions.filter((id) => id !== action.payload);
    },
    setLoading: (state, action: { payload: boolean }) => {
      state.loading = action.payload;
    },
    setError: (state, action: { payload: string | null }) => {
      state.error = action.payload;
    },
    clearMarketData: (state) => {
      state.liveQuotes = {};
      // Reset watchlist groups to empty
      state.watchlistGroups = {};
      state.currentGroup = null;
      state.subscriptions = [];
      state.searchResults = [];
      state.searchQuery = '';
      state.error = null;
      state.watchlistError = null;
    },
    // Optimistic update: Add instrument to watchlist (before API call)
    optimisticAddToWatchlist: (state, action: { payload: string }) => {
      if (state.currentGroup && state.watchlistGroups[state.currentGroup]) {
        const group = state.watchlistGroups[state.currentGroup];
        if (!group.instruments.includes(action.payload)) {
          group.instruments.push(action.payload);
        }
      }
    },
    // Optimistic update: Remove instrument from watchlist (before API call)
    optimisticRemoveFromWatchlist: (state, action: { payload: string }) => {
      if (state.currentGroup && state.watchlistGroups[state.currentGroup]) {
        const group = state.watchlistGroups[state.currentGroup];
        group.instruments = group.instruments.filter((id) => id !== action.payload);
      }
    },
    // Rollback: Revert optimistic add
    rollbackAddToWatchlist: (state, action: { payload: string }) => {
      if (state.currentGroup && state.watchlistGroups[state.currentGroup]) {
        const group = state.watchlistGroups[state.currentGroup];
        group.instruments = group.instruments.filter((id) => id !== action.payload);
      }
    },
    // Rollback: Revert optimistic remove
    rollbackRemoveFromWatchlist: (state, action: { payload: string }) => {
      if (state.currentGroup && state.watchlistGroups[state.currentGroup]) {
        const group = state.watchlistGroups[state.currentGroup];
        if (!group.instruments.includes(action.payload)) {
          group.instruments.push(action.payload);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Search instruments
      .addCase(searchInstruments.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchInstruments.fulfilled, (state, action) => {
        state.searchResults = action.payload.instruments;
        state.searchQuery = action.payload.query;
        // Update normalized instruments
        action.payload.instruments.forEach((inst) => {
          state.instruments[inst.instrument_id] = inst;
        });
        state.searchLoading = false;
        state.error = null;
      })
      .addCase(searchInstruments.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload as string;
      })
      // Load watchlist groups
      .addCase(loadWatchlistGroups.pending, (state) => {
        state.watchlistLoading = true;
        state.watchlistError = null;
      })
      .addCase(loadWatchlistGroups.fulfilled, (state, action) => {
        // Map backend groups to frontend format
        const groups: Record<string, WatchlistGroup> = {};
        action.payload.forEach((group: any) => {
          // Preserve previously loaded instruments/itemsLoaded if the group already exists
          const existing = state.watchlistGroups[group.id];
          groups[group.id] = {
            id: group.id,
            name: group.name,
            instruments: existing?.instruments ?? [],
            item_count: group.item_count ?? 0,   // populated by COUNT JOIN on backend
            is_default: group.is_default || false,
            itemsLoaded: existing?.itemsLoaded ?? false,
          };
        });
        state.watchlistGroups = groups;

        // Set current group to default group if it exists, otherwise first group
        if (Object.keys(groups).length > 0) {
          const defaultGroup = Object.values(groups).find(g => g.is_default);
          state.currentGroup = defaultGroup?.id || Object.keys(groups)[0];
        }

        state.watchlistLoading = false;
        state.watchlistError = null;
      })
      .addCase(loadWatchlistGroups.rejected, (state, action) => {
        state.watchlistLoading = false;
        state.watchlistError = action.payload as string;
      })
      // Load watchlist items
      .addCase(loadWatchlistItems.fulfilled, (state, action) => {
        const { groupId, items, instruments } = action.payload;
        // eslint-disable-next-line no-console
        console.log('loadWatchlistItems.fulfilled: Processing', items.length, 'items and', instruments?.length || 0, 'instruments for group', groupId);
        if (state.watchlistGroups[groupId]) {
          state.watchlistGroups[groupId].instruments = items.map((item: any) => item.instrument_id);
          state.watchlistGroups[groupId].item_count = items.length;
          // Mark this group as having had its items fetched — prevents infinite re-fetch
          // for groups that genuinely have 0 instruments.
          state.watchlistGroups[groupId].itemsLoaded = true;
          // eslint-disable-next-line no-console
          console.log('loadWatchlistItems.fulfilled: Updated group instruments:', state.watchlistGroups[groupId].instruments);
        }
        // Add fetched instruments to the normalized instruments state
        if (instruments && instruments.length > 0) {
          instruments.forEach((inst) => {
            if (inst.instrument_id) {
              state.instruments[inst.instrument_id] = inst;
              // eslint-disable-next-line no-console
              console.log('loadWatchlistItems.fulfilled: Added instrument to state:', inst.instrument_id, inst.symbol);
            }
          });
        } else {
          // eslint-disable-next-line no-console
          console.warn('loadWatchlistItems.fulfilled: No instruments to add to state');
        }
      })
      // Create watchlist group
      .addCase(createWatchlistGroup.pending, (state) => {
        state.watchlistLoading = true;
        state.watchlistError = null;
      })
      .addCase(createWatchlistGroup.fulfilled, (state, action) => {
        const group = action.payload;
        state.watchlistGroups[group.id] = {
          id: group.id,
          name: group.name,
          instruments: [],
          item_count: 0,
          is_default: group.is_default || false,
          itemsLoaded: false,
        };
        // If this is the default group, set it as current
        if (group.is_default) {
          state.currentGroup = group.id;
        } else if (!state.currentGroup) {
          // If no current group, set this one
          state.currentGroup = group.id;
        }
        state.watchlistLoading = false;
        state.watchlistError = null;
      })
      .addCase(createWatchlistGroup.rejected, (state, action) => {
        state.watchlistLoading = false;
        state.watchlistError = action.payload as string;
      })
      // Update watchlist group
      .addCase(updateWatchlistGroup.pending, (state) => {
        state.watchlistLoading = true;
        state.watchlistError = null;
      })
      .addCase(updateWatchlistGroup.fulfilled, (state, action) => {
        const group = action.payload;
        if (state.watchlistGroups[group.id]) {
          state.watchlistGroups[group.id].name = group.name;
          state.watchlistGroups[group.id].is_default = group.is_default || false;
          // If this group is now default, set it as current
          if (group.is_default) {
            state.currentGroup = group.id;
          }
        }
        state.watchlistLoading = false;
        state.watchlistError = null;
      })
      .addCase(updateWatchlistGroup.rejected, (state, action) => {
        state.watchlistLoading = false;
        state.watchlistError = action.payload as string;
      })
      // Delete watchlist group
      .addCase(deleteWatchlistGroupAsync.pending, (state) => {
        state.watchlistLoading = true;
        state.watchlistError = null;
      })
      .addCase(deleteWatchlistGroupAsync.fulfilled, (state, action) => {
        const groupId = action.payload;
        const groupCount = Object.keys(state.watchlistGroups).length;

        // Don't allow deleting if it's the last group
        if (groupCount <= 1) {
          state.watchlistLoading = false;
          return;
        }

        // If deleting the current group, switch to another group
        if (state.currentGroup === groupId) {
          const remainingGroups = Object.keys(state.watchlistGroups).filter(id => id !== groupId);
          state.currentGroup = remainingGroups[0] || null;
        }

        // Delete the group
        delete state.watchlistGroups[groupId];
        state.watchlistLoading = false;
        state.watchlistError = null;
      })
      .addCase(deleteWatchlistGroupAsync.rejected, (state, action) => {
        state.watchlistLoading = false;
        state.watchlistError = action.payload as string;
      })
      // Add watchlist item
      // NOTE: optimisticAddToWatchlist already pushed the instrument_id before the API call.
      // The fulfilled handler must NOT push again — it would create a duplicate entry if
      // the backend returns a UUID in a different case or format than the optimistic value.
      // Instead, we only update item_count for accuracy.
      .addCase(addWatchlistItem.fulfilled, (state, action) => {
        const { groupId } = action.payload;
        if (state.watchlistGroups[groupId]) {
          state.watchlistGroups[groupId].item_count = state.watchlistGroups[groupId].instruments.length;
        }
      })
      .addCase(addWatchlistItem.rejected, (state, action) => {
        state.watchlistError = action.payload as string;
      })
      // Remove watchlist item
      .addCase(removeWatchlistItem.fulfilled, (state, action) => {
        const { groupId, instrumentId } = action.payload;
        if (state.watchlistGroups[groupId]) {
          const group = state.watchlistGroups[groupId];
          group.instruments = group.instruments.filter((id) => id !== instrumentId);
        }
      })
      .addCase(removeWatchlistItem.rejected, (state, action) => {
        state.watchlistError = action.payload as string;
      });
  },
});

export const {
  updateQuote,
  updateQuotes,
  registerInstruments,
  addToWatchlist,
  removeFromWatchlist,
  setWatchlistGroup,
  addWatchlistGroup,
  updateWatchlistGroupName,
  deleteWatchlistGroup,
  clearSearchResults,
  addSubscription,
  removeSubscription,
  setLoading,
  setError,
  clearMarketData,
  optimisticAddToWatchlist,
  optimisticRemoveFromWatchlist,
  rollbackAddToWatchlist,
  rollbackRemoveFromWatchlist,
} = marketDataSlice.actions;
export default marketDataSlice.reducer;

